import React from 'react';
import type { ILocation, WidgetProps } from '@wix/yoshi-flow-editor';
import { Menu } from 'root/components/Menus/Menu/Menu';
import type { PopulatedMenu } from 'root/apiTypes';
import { dataHooks } from 'root/dataHooks';
import type { NavigateToFn } from 'root/clientTypes';
import { useMenusLogic } from './Widget.logic';
import { AppWrapper } from 'root/components/Menus/AppWrapper/AppWrapper';
import { classes, st } from './Widget.st.css';
import { Navigation } from 'root/components/Menus/Navigation/Navigation';
import { MenuState } from 'root/components/Menus/MenuState/MenuState';
import { WowImage, ImageResizeOptions } from 'wix-ui-tpa/cssVars';
import { DEFAULT_MENUS_SIDE_IMAGE } from 'root/assets/images/MenusSideImage';
import { Locations } from 'root/components/Menus/Locations/Locations';
import type { LocationsList } from 'root/types/locations';
import { NavigationWithSections } from 'root/components/Menus/NavigationWithSections/NavigationWithSections';
import { getImageSize } from 'root/utils/images';
import type { DiscountByItemId } from '@wix/restaurants-normalizers';

export type ControllerProps = {
  menus: PopulatedMenu[];
  locations?: LocationsList;
  navigateTo: NavigateToFn;
  messageError?: string;
  url: ILocation['url'];
  fitToContentHeight: true;
  initialActiveTab: number;
  initialLocation?: string;
  discountInfoByItemId?: DiscountByItemId;
  showPaywallPlaceholder?: boolean;
};

export const Menus = ({
  menus,
  locations,
  navigateTo,
  messageError,
  url,
  initialActiveTab,
  initialLocation,
  discountInfoByItemId = {},
  showPaywallPlaceholder,
}: WidgetProps<ControllerProps>) => {
  const {
    t,
    activeTab,
    displayableMenus,
    setActiveTab,
    menusSideImage,
    displayableLocations,
    showLocations,
    currentLocation,
    setCurrentLocation,
    isRow,
    hasSectionNavigation,
    shouldShowTabs,
  } = useMenusLogic({
    menus,
    locations,
    initialActiveTab,
    initialLocation,
  });

  const renderMenuState = (content: string) => (
    <div className={st(classes.root)}>
      <MenuState content={content} />
    </div>
  );

  if (showPaywallPlaceholder) {
    return renderMenuState(t('component.menu.paywall-placeholder'));
  }

  if (messageError) {
    return renderMenuState(messageError);
  }
  const menusSideImageSize = getImageSize(menusSideImage);
  return displayableMenus?.length > 0 ? (
    <div>
      <div className={st(classes.root)} data-hook={dataHooks.menus.container}>
        <div className={classes.imageContainer}>
          <WowImage
            shouldUseLQIP
            className={classes.sideBySideImage}
            data-hook={dataHooks.menu.sideImage}
            resize={ImageResizeOptions.cover}
            sourceHeight={menusSideImageSize.height}
            sourceWidth={menusSideImageSize.width}
            src={menusSideImage.url ?? DEFAULT_MENUS_SIDE_IMAGE}
          />
        </div>
        {hasSectionNavigation ? (
          <>
            {showLocations && (
              <Locations
                currentLocation={currentLocation ?? ''}
                currentUrl={url}
                locations={displayableLocations}
                navigateTo={navigateTo}
                setActiveTab={setActiveTab}
                setCurrentLocation={setCurrentLocation}
              />
            )}
            <NavigationWithSections
              activeMenuIndex={activeTab}
              currentUrl={url}
              menus={displayableMenus}
              navigateTo={navigateTo}
              setActiveItem={setActiveTab}
            />
          </>
        ) : (
          <div
            className={`${classes.headerContainer} ${isRow && classes.headerContainerRow} ${
              hasSectionNavigation ? classes.sectionNavigation : ''
            }`}
          >
            {showLocations && (
              <Locations
                currentLocation={currentLocation ?? ''}
                currentUrl={url}
                locations={displayableLocations}
                navigateTo={navigateTo}
                setActiveTab={setActiveTab}
                setCurrentLocation={setCurrentLocation}
              />
            )}
            {shouldShowTabs && (
              <Navigation
                activeItem={activeTab}
                currentUrl={url}
                isRow={isRow}
                menus={displayableMenus}
                navigateTo={navigateTo}
                setActiveItem={setActiveTab}
              />
            )}
          </div>
        )}
        <div className={classes.menuContent}>
          <Menu discountInfoByItemId={discountInfoByItemId} menu={displayableMenus[activeTab]} />
        </div>
      </div>
    </div>
  ) : (
    renderMenuState(t('component.menu.empty-state'))
  );
};

export const Widget = (props: WidgetProps<ControllerProps>) => {
  return (
    <AppWrapper>
      <Menus {...props} />
    </AppWrapper>
  );
};
